// Fantasy prizes. Single source of truth for what is up for grabs and who is currently holding it,
// so the hub, the create page and the guide can't drift from each other — or from the announcement.

/** The season the prizes below apply to. Older seasons had none, so they render no prize block. */
export const PRIZE_SEASON = 33;

/**
 * Fantasy runs one leaderboard per division, and so do the prizes: every division pays out its own
 * first and second place. Flip this to 'overall' if it ever becomes a single cross-division race —
 * `prizeWinners` is the only place that has to care.
 */
export type PrizeScope = 'overall' | 'perDivision';
export const PRIZE_SCOPE: PrizeScope = 'perDivision';

export interface Prize {
    /** Which position in the division wins it. */
    placement: number;
    /** Key under `pages:fantasy` holding the prize text. */
    key: string;
    tone: 'gold' | 'silver';
}

export const PRIZES: Prize[] = [
    { placement: 1, key: 'prizeFirst', tone: 'gold' },
    { placement: 2, key: 'prizeSecond', tone: 'silver' },
];

/** The placements that win something, for callers that only need to test a row. */
export const PRIZE_PLACEMENTS: number[] = PRIZES.map((prize) => prize.placement);

/** Just enough of a leaderboard row for the prize maths; the hub's richer rows satisfy it. */
type RankedTeam = {
    account: { steamID: string; playerName: string };
    points: number;
    placement: number;
};

/** The hub's leaderboard response: top ten teams per division, keyed by division. */
export type PrizeLeaderboard = Record<string, RankedTeam[]>;

export interface PrizeStanding {
    /** The prize position this team currently holds, i.e. its placement in the division. */
    placement: number;
    steamId: string;
    playerName: string;
    division: string;
    points: number;
}

/**
 * Who is in line for each of one division's prizes right now.
 *
 * Empty until somebody in that division has actually scored: before the first matchday every team
 * sits on 0, and naming whichever of them happens to sort first would read as "this person is
 * winning". Also empty for a division nobody has entered.
 */
export const prizeWinners = (
    leaderboard: PrizeLeaderboard,
    division: string | null,
): PrizeStanding[] => {
    const entries = (division && leaderboard[division]) || [];
    if (!entries.length) return [];
    if (Math.max(...entries.map((entry) => entry.points)) <= 0) return [];

    return PRIZES.flatMap((prize) => {
        // The API appends the signed-in visitor's own team after the top ten with its real
        // placement, so matching on placement stays unambiguous.
        const team = entries.find((entry) => entry.placement === prize.placement);
        if (!team) return [];
        return [{
            placement: prize.placement,
            steamId: team.account.steamID,
            playerName: team.account.playerName,
            division: division as string,
            points: team.points,
        }];
    });
};
